import { useRef, useEffect, useCallback } from "react";
import { observer } from "mobx-react-lite";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "highcharts/modules/xrange";
import { ChartType } from "@/stores/ui/Chart";
import { ChartControls } from "./ChartControls";
import { Spinner } from "@/components/Spinner";
import { useChartStore } from "@/hooks/useChartStore";

const SYNC_TOLERANCE_MS = 1000;
const RAIN_COLOR = "#06b6d4";
const RAIN_SERIES_ID = "rain-overlay";
const rainAxisLabel = (unit: string) => `Rain (${unit})`;
const bandId = (rangeKey: string, idx: number) => `band-${rangeKey}-${idx}`;
const formatGraphLabel = (name: string, unitSymbol?: string) => (unitSymbol ? `${name} (${unitSymbol})` : name);

/** Binary search to find the closest point to a given x value */
const findClosestPoint = (points: Highcharts.Point[], xValue: number): Highcharts.Point | undefined => {
	if (!points.length) return undefined;

	let left = 0;
	let right = points.length - 1;

	while (left < right) {
		const mid = Math.floor((left + right) / 2);
		if (points[mid].x < xValue) {
			left = mid + 1;
		} else {
			right = mid;
		}
	}

	// Check neighbors to find the actual closest
	const candidates = [points[left - 1], points[left], points[left + 1]].filter(Boolean);
	let closest: Highcharts.Point | undefined;
	let minDiff = Infinity;

	for (const p of candidates) {
		const diff = Math.abs(p.x - xValue);
		if (diff < minDiff) {
			minDiff = diff;
			closest = p;
		}
	}

	return minDiff <= SYNC_TOLERANCE_MS ? closest : undefined;
};

export interface RainOverlay {
	data: [number, number][];
	unit: string;
	pointRangeMs?: number;
}

/** Align multiple series to shared timestamps via linear interpolation */
function alignSeriesData(allData: Array<[number, number | null][]>): Array<[number, number | null][]> {
	if (allData.length <= 1) return allData;

	// Collect all unique timestamps from all series
	const timestampSet = new Set<number>();
	allData.forEach(data => data.forEach(([x]) => timestampSet.add(x)));
	const timestamps = [...timestampSet].sort((a, b) => a - b);

	return allData.map(data => {
		if (data.length === 0) return timestamps.map(t => [t, null] as [number, number | null]);

		// Fast lookup for exact matches
		const dataMap = new Map<number, number | null>();
		data.forEach(([x, y]) => dataMap.set(x, y));

		return timestamps.map(t => {
			if (dataMap.has(t)) return [t, dataMap.get(t)!] as [number, number | null];

			// Outside this series' range
			if (t < data[0][0] || t > data[data.length - 1][0]) {
				return [t, null] as [number, number | null];
			}

			// Binary search for surrounding points
			let lo = 0;
			let hi = data.length - 1;
			while (lo < hi - 1) {
				const mid = (lo + hi) >> 1;
				if (data[mid][0] <= t) lo = mid;
				else hi = mid;
			}

			const [x1, y1] = data[lo];
			const [x2, y2] = data[hi];

			if (y1 === null || y2 === null || x1 === x2) return [t, null] as [number, number | null];

			const ratio = (t - x1) / (x2 - x1);
			return [t, y1 + ratio * (y2 - y1)] as [number, number | null];
		});
	});
}

export interface ChartRange {
	key: string;
	name: string;
	color: string;
	opacity?: number;
	hiddenFromLegend?: boolean;
	data: { x: number; x2: number }[];
	onRangeClick?: (item: { x: number; x2: number }) => void;
}

export interface ChartProps {
	structureId: Dryp.StructureId;
	parameters: Dryp.Parameter[] | Dryp.StructureParameter[];
	from: string;
	to: string;
	title?: string;
	yAxisTitle?: string;
	xAxisType?: "datetime" | "linear" | "category";
	defaultType?: ChartType;
	showControls?: boolean;
	className?: string;
	syncId?: string;
	height?: number;
	rainOverlay?: RainOverlay;
	yAxisReversed?: boolean;
	ranges?: ChartRange[];
	legendExtra?: React.ReactNode;
}

export const Chart: React.FC<ChartProps> = observer(
	({
		structureId,
		parameters,
		from,
		to,
		title,
		yAxisTitle,
		xAxisType = "datetime",
		defaultType = "spline",
		showControls = true,
		className,
		syncId,
		rainOverlay,
		yAxisReversed = false,
		ranges,
		height,
		legendExtra,
	}) => {
		const chartRef = useRef<HighchartsReact.RefObject>(null);
		const containerRef = useRef<HTMLDivElement>(null);
		const store = useChartStore({ structureId, defaultType, height });

		useEffect(() => {
			parameters.forEach(param => {
				store.addGraph(param);
			});
			store.fetchAll(from, to);
		}, [structureId, parameters.map(param => param.id).join(","), from, to]);

		// Sync range data to store
		const rangesKey = ranges?.map(r => `${r.key}:${r.data.length}`).join(",") ?? "";
		useEffect(() => {
			if (ranges) {
				ranges.forEach(r => {
					const rangeStore = store.addRange(r.key, r.name, r.color);
					rangeStore.setData(r.data);
				});
			}
		}, [rangesKey]);

		const handleChartCreated = useCallback(
			(chart: Highcharts.Chart) => {
				store.zoom.updateChart(chart);
				// Tag chart with syncId for synchronization
				if (syncId) {
					(chart as any).syncId = syncId;
				}
			},
			[syncId, store.zoom],
		);

		// Clean up chart on unmount
		useEffect(() => {
			return () => {
				store.zoom.updateChart(null);
			};
		}, []);

		// Handle mouse move for synchronized tooltips
		const handleMouseMove = useCallback(
			(e: React.MouseEvent<HTMLDivElement>) => {
				if (!syncId) return;

				const chart = chartRef.current?.chart;
				if (!chart) return;

				// Normalize the event
				const event = chart.pointer.normalize(e.nativeEvent);

				// Find hovered point using Highcharts' built-in searchPoint method
				// This matches Highcharts' native tooltip behavior exactly
				let hoveredPoint: Highcharts.Point | undefined;
				for (const series of chart.series) {
					if (!series.visible) continue;
					const point = series.searchPoint(event, true);
					if (point) {
						hoveredPoint = point;
						break;
					}
				}

				if (!hoveredPoint) return;

				// Use the exact x value (timestamp) from the hovered point
				const xValue = hoveredPoint.x;

				// Sync all charts with the same syncId
				Highcharts.charts.forEach(syncedChart => {
					if (!syncedChart || (syncedChart as any).syncId !== syncId) return;
					if (syncedChart === chart) return;

					const points: Highcharts.Point[] = [];
					syncedChart.series.forEach(series => {
						if (!series.visible || !series.points?.length) return;
						const point = findClosestPoint(series.points, xValue);
						if (point) {
							points.push(point);
							point.setState("hover");
						}
					});

					if (points.length > 0) {
						// Draw crosshair using the actual point position
						syncedChart.xAxis[0].drawCrosshair(undefined, points[0]);
						// Show tooltip with all points at this timestamp
						syncedChart.tooltip.refresh(points);
					} else {
						// No exact match - draw crosshair at position but hide tooltip
						const syncedXAxis = syncedChart.xAxis[0];
						const xPos = syncedXAxis.toPixels(xValue, false);
						syncedXAxis.drawCrosshair({ chartX: xPos, chartY: 0 } as Highcharts.PointerEventObject);
						syncedChart.tooltip.hide();
					}
				});
			},
			[syncId],
		);

		const handleMouseLeave = useCallback(() => {
			if (!syncId) return;

			Highcharts.charts.forEach(chart => {
				if (!chart || (chart as any).syncId !== syncId) return;
				chart.tooltip.hide();
				chart.xAxis[0].hideCrosshair();
				// Reset hover state on all points
				chart.series.forEach(series => {
					series.points?.forEach(point => {
						point.setState("");
					});
				});
			});
		}, [syncId]);

		// Map each unique unit to a yAxis index for dual-axis support
		// Group graphs by unitSymbol so graphs sharing the same unit share an axis
		const graphToAxisIndex = new Map<string, number>();
		const axisGroups: Array<(typeof store.graphs)[number][]> = [];
		store.graphs.forEach(graph => {
			const key = graph.unitSymbol || String(graph.id);
			if (!graphToAxisIndex.has(key)) {
				graphToAxisIndex.set(key, axisGroups.length);
				axisGroups.push([]);
			}
			axisGroups[graphToAxisIndex.get(key)!].push(graph);
		});
		// Show names-left / units-right layout whenever there are 2+ graphs
		const hasDifferentUnits = axisGroups.length > 1;

		// Align data points across all graphs so the shared tooltip can show all series together
		const rawDataArrays = store.graphs.map(graph => graph.data as Array<[number, number | null]>);
		const alignedDataArrays = alignSeriesData(rawDataArrays);

		// Include all graphs but mark hidden ones as invisible so Highcharts
		// still uses their data for axis range calculation (needed for plotBands)
		const graphData: Highcharts.SeriesOptionsType[] = store.graphs.map((graph, index) => {
			return {
				type: store.chartType as ChartType,
				name: formatGraphLabel(graph.name, graph.unitSymbol),
				data: alignedDataArrays[index],
				color: graph.color,
				visible: graph.visible,
				showInLegend: false,
				id: String(graph.id),
				yAxis: hasDifferentUnits ? (graphToAxisIndex.get(graph.unitSymbol || String(graph.id)) ?? 0) : 0,
				tooltip: {
					valueDecimals: graph.decimals,
				},
			};
		});

		// Each band gets a stable id so hover can mutate its fill without a chart redraw.
		const plotBands: Highcharts.XAxisPlotBandsOptions[] = (ranges ?? []).flatMap(range => {
			const rangeStore = store.getRange(range.key);
			if (!rangeStore?.visible) return [];
			const baseOpacity = range.opacity ?? 0.15;
			const bandColor = Highcharts.color(rangeStore.color).setOpacity(baseOpacity).get() as string;
			return rangeStore.data.map((d, idx) => ({
				id: bandId(range.key, idx),
				from: d.x,
				to: d.x2,
				color: bandColor,
				zIndex: 5,
				...(range.onRangeClick
					? {
							className: "cursor-pointer",
							events: { click: () => range.onRangeClick!({ x: d.x, x2: d.x2 }) },
						}
					: {}),
			}));
		});

		const visibleGraphsSnapshot = store.graphs
			.filter(g => g.visible)
			.map(g => ({
				name: formatGraphLabel(g.name, g.unitSymbol),
				color: g.color,
				decimals: g.decimals,
				id: String(g.id),
			}));
		const visibleRangesSnapshot = (ranges ?? [])
			.filter(r => !r.hiddenFromLegend)
			.flatMap(r => {
				const rangeStore = store.getRange(r.key);
				if (!rangeStore?.visible) return [];
				return [{ name: rangeStore.name, color: rangeStore.color, data: rangeStore.data }];
			});

		// When no graphs are visible, derive xAxis range from plotBands so they still render
		const noVisibleGraphs = store.visibleGraphs.length === 0;
		let rangeXMin: number | undefined;
		let rangeXMax: number | undefined;
		if (noVisibleGraphs && plotBands.length > 0) {
			rangeXMin = Math.min(...plotBands.map(b => b.from as number));
			rangeXMax = Math.max(...plotBands.map(b => b.to as number));
		}

		const firstGraph = store.graphs[0];

		// Use parameter name as title if not provided; show all graph names when multiple
		const chartTitle =
			title ?? (store.graphs.length > 1 ? store.graphs.map(g => g.name).join(" / ") : firstGraph?.name);

		// Build yAxis config
		// 2+ graphs: left axis = parameter names, right axis = unit symbols + names
		// 1 graph: single axis with "Name (unit)"

		const hasRain = rainOverlay && rainOverlay.data.length > 0;
		const rainAxisMax = hasRain
			? rainOverlay.data.reduce((max, d) => (d[1] > max ? d[1] : max), 0.1) * 4
			: undefined;
		const rainPointRange = hasRain ? rainOverlay.pointRangeMs : undefined;

		const allSeries: Highcharts.SeriesOptionsType[] = hasRain
			? [
					...graphData,
					{
						type: "column",
						id: RAIN_SERIES_ID,
						name: rainAxisLabel(rainOverlay.unit),
						data: rainOverlay.data,
						yAxis: 1,
						color: "rgba(6, 182, 212, 0.35)",
						borderWidth: 0,
						borderRadius: 1,
						pointPadding: 0.3,
						groupPadding: 0.05,
						pointRange: rainPointRange,
						maxPointWidth: 8,
						tooltip: {
							valueDecimals: 1,
						},
					},
				]
			: graphData;

		const rainTooltipSnapshot = hasRain ? { unit: rainOverlay.unit, id: RAIN_SERIES_ID } : null;

		const uniqueUnits = store.graphs
			.map(g => g.unit)
			.filter((value, index, array) => array.indexOf(value) === index);
		const hasMultipleGraphs = store.graphs.length > 1;

		const uniqueUnitSymbols = store.graphs
			.map(graph => graph.unitSymbol)
			.filter((value, index, array) => array.indexOf(value) === index);
		const sharedUnitSymbol = uniqueUnitSymbols.length === 1 ? uniqueUnitSymbols[0] : null;

		const primaryYAxis: Highcharts.YAxisOptions = {
			title: {
				text:
					yAxisTitle ??
					(hasMultipleGraphs
						? (sharedUnitSymbol ??
							store.graphs.map(g => formatGraphLabel(g.name, g.unitSymbol)).join(" / "))
						: firstGraph
							? formatGraphLabel(firstGraph.name, firstGraph.unitSymbol)
							: undefined),
			},
			reversed: yAxisReversed,
			labels: {
				formatter: function () {
					const decimals = (hasMultipleGraphs ? axisGroups[0]?.[0]?.decimals : firstGraph?.decimals) ?? 2;
					return Number(this.value).toFixed(decimals);
				},
			},
		};

		const multiAxisConfig: Highcharts.YAxisOptions[] = [
			{ ...primaryYAxis, opposite: false, reversed: false },
			{
				title: { text: uniqueUnits.join(" / ") },
				opposite: true,
				reversed: yAxisReversed,
				gridLineWidth: 0,
				...(hasDifferentUnits ? {} : { linkedTo: 0 }),
				labels: {
					formatter: function () {
						const rightAxisGroup = hasDifferentUnits ? axisGroups[1] : axisGroups[0];
						const decimals = rightAxisGroup?.[0]?.decimals ?? 2;
						return Number(this.value).toFixed(decimals);
					},
				},
			},
		];

		let yAxis: Highcharts.YAxisOptions | Highcharts.YAxisOptions[];
		if (hasRain) {
			yAxis = [
				primaryYAxis,
				{
					title: { text: rainAxisLabel(rainOverlay.unit), style: { color: RAIN_COLOR } },
					opposite: true,
					reversed: true,
					max: rainAxisMax,
					gridLineWidth: 0,
					labels: {
						style: { color: RAIN_COLOR },
						formatter: function () {
							return Number(this.value).toFixed(1);
						},
					},
				},
			];
		} else if (hasMultipleGraphs) {
			yAxis = multiAxisConfig;
		} else {
			yAxis = primaryYAxis;
		}

		const options: Highcharts.Options = {
			chart: {
				zooming: { type: "x" },
				resetZoomButton: {
					position: {
						x: -10000,
						y: -10000,
					},
				},
				height: store.chartHeight,
				animation: false,
				backgroundColor: "transparent",
			},
			title: { text: undefined },
			plotOptions: {
				series: {
					animation: {
						duration: 800,
					},
					states: {
						hover: {
							lineWidthPlus: 0,
						},
					},
					marker: {
						enabled: false,
						states: {
							hover: {
								enabled: true,
								radius: 4,
							},
						},
					},
				},
				spline: {
					lineWidth: 2,
				},
				line: {
					lineWidth: 2,
				},
				area: {
					fillOpacity: 0.3,
					lineWidth: 2,
				},
				areaspline: {
					fillOpacity: 0.3,
					lineWidth: 2,
				},
			},
			xAxis: {
				type: xAxisType,
				min: store.zoom.range.min !== null ? store.zoom.range.min : rangeXMin,
				max: store.zoom.range.max !== null ? store.zoom.range.max : rangeXMax,
				crosshair: { width: 1, color: "rgba(128, 128, 128, 0.5)" },
				plotBands,
				dateTimeLabelFormats: {
					millisecond: "%H:%M:%S.%L",
					second: "%H:%M:%S",
					minute: "%H:%M",
					hour: "%H:%M",
					day: "%e. %b",
					week: "%e. %b",
					month: "%b '%y",
					year: "%Y",
				},
				events: {
					afterSetExtremes: event => store.zoom.handleZoomChange(event),
				},
			},
			yAxis,
			tooltip: {
				shared: true,
				followPointer: true,
				animation: false,
				hideDelay: 0,
				xDateFormat: xAxisType === "datetime" ? "%Y-%m-%d %H:%M" : undefined,
				backgroundColor: "#ffffff",
				borderColor: "hsl(var(--border))",
				borderRadius: 8,
				shadow: true,
				style: {
					color: "hsl(var(--primary))",
					fontSize: "12px",
				},
				useHTML: true,
				formatter: function () {
					const x = this.x as number;
					const row = (color: string, label: string, value: string) =>
						'<div style="display:flex;align-items:center;gap:6px;padding:2px 0;">' +
						`<span style="display:inline-block;width:10px;height:10px;background-color:${color};border-radius:2px;"></span>` +
						`<span style="color:hsl(var(--muted-foreground));">${label}:</span> ` +
						`<span style="font-weight:500;color:hsl(var(--primary));">${value}</span>` +
						"</div>";

					let html =
						xAxisType === "datetime"
							? `<div style="font-size:11px;color:hsl(var(--muted-foreground));margin-bottom:4px;">${Highcharts.dateFormat("%Y-%m-%d %H:%M", x)}</div>`
							: "";

					// Always show all visible series — use "—" when no data
					const pointsBySeriesId = new Map<string, Highcharts.Point>();
					(this.points ?? []).forEach(pt => {
						pointsBySeriesId.set(pt.series.options.id as string, pt);
					});

					visibleGraphsSnapshot.forEach(graph => {
						const pt = pointsBySeriesId.get(graph.id);
						if (pt && pt.y != null) {
							html += row(graph.color, graph.name, Highcharts.numberFormat(pt.y, graph.decimals));
						} else {
							html += row(graph.color, graph.name, "—");
						}
					});

					// Rain overlay row
					if (rainTooltipSnapshot) {
						const rainPt = pointsBySeriesId.get(rainTooltipSnapshot.id);
						const label = rainAxisLabel(rainTooltipSnapshot.unit);
						const value = rainPt?.y != null ? Highcharts.numberFormat(rainPt.y, 1) : "—";
						html += row(RAIN_COLOR, label, value);
					}

					// Always show all visible ranges — use "—" when not active
					visibleRangesSnapshot.forEach(range => {
						const active = range.data.find(d => x >= d.x && x <= d.x2);
						if (active) {
							const ms = active.x2 - active.x;
							const totalSec = Math.round(ms / 1000);
							const h = Math.floor(totalSec / 3600);
							const m = Math.floor((totalSec % 3600) / 60);
							const duration = h > 0 ? `${h}h ${m}m` : `${m}m`;
							const from = Highcharts.dateFormat("%m-%d %H:%M", active.x);
							const to = Highcharts.dateFormat("%m-%d %H:%M", active.x2);
							html += row(range.color, range.name, `${duration} (${from} — ${to})`);
						} else {
							html += row(range.color, range.name, "—");
						}
					});

					return html || false;
				},
			},
			credits: {
				enabled: false,
			},
			accessibility: {
				enabled: false,
			},
			legend: {
				enabled: false,
			},
			series: allSeries,
		};

		// Determine message state — ranges count as data too
		const hasVisibleContent = store.hasData || store.visibleRanges.length > 0;
		const showMessage =
			store.loaded && !store.loading && (store.hasError || store.isNotConfigured || !hasVisibleContent);
		const errorMessage = store.hasError && !store.isNotConfigured ? String(store.graphs[0]?.error) : null;

		return (
			<div
				ref={containerRef}
				className={className}
				onMouseMove={syncId ? handleMouseMove : undefined}
				onMouseLeave={syncId ? handleMouseLeave : undefined}
			>
				{/* Chart Header */}
				<div className="px-3 pt-3 pb-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
					<div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 min-w-0">
						<h4 className="text-sm font-medium shrink-0">{chartTitle}</h4>
						{showControls && <ChartControls store={store} variant="refresh" />}
						{hasMultipleGraphs &&
							store.graphs.map(graph => (
								<button
									key={graph.id}
									type="button"
									onClick={() => graph.toggleVisible()}
									className={`flex items-center gap-1.5 text-xs transition-opacity cursor-pointer select-none ${
										graph.visible
											? "text-muted-foreground hover:text-foreground"
											: "text-muted-foreground/50 line-through"
									}`}
									aria-pressed={graph.visible}
									title={graph.visible ? `Hide ${graph.name}` : `Show ${graph.name}`}
								>
									<span
										className="inline-block h-3 w-3 rounded-sm shrink-0"
										style={{
											backgroundColor: graph.visible ? graph.color : "transparent",
											border: graph.visible ? undefined : `1px solid ${graph.color}`,
										}}
									/>
									{graph.name}
								</button>
							))}
						{ranges
							?.filter(r => !r.hiddenFromLegend)
							.map(r => {
								const rangeStore = store.getRange(r.key);
								const isVisible = rangeStore?.visible ?? true;
								const baseOpacity = r.opacity ?? 0.15;
								const swatchColor = Highcharts.color(r.color).setOpacity(0.5).get() as string;
								const applyBandFill = (opacity: number) => {
									const chart = chartRef.current?.chart;
									if (!chart) return;
									const fill = Highcharts.color(r.color).setOpacity(opacity).get() as string;
									const prefix = `band-${r.key}-`;
									// plotLinesAndBands is internal Highcharts state; not in public TS types.
									const bands = (
										chart.xAxis[0] as unknown as {
											plotLinesAndBands?: Array<{
												id?: string;
												svgElem?: { attr: (attrs: Record<string, string>) => void };
											}>;
										}
									)?.plotLinesAndBands;
									bands?.forEach(band => {
										if (typeof band.id === "string" && band.id.startsWith(prefix)) {
											band.svgElem?.attr({ fill });
										}
									});
								};
								return (
									<button
										key={r.key}
										type="button"
										onClick={() => rangeStore?.toggleVisible()}
										onMouseEnter={() => isVisible && applyBandFill(Math.min(1, baseOpacity + 0.35))}
										onMouseLeave={() => isVisible && applyBandFill(baseOpacity)}
										className={`flex items-center gap-1.5 text-xs transition-opacity cursor-pointer select-none ${
											isVisible
												? "text-muted-foreground hover:text-foreground"
												: "text-muted-foreground/50 line-through"
										}`}
										aria-pressed={isVisible}
										title={isVisible ? `Hide ${r.name}` : `Show ${r.name}`}
									>
										<span
											className="inline-block h-3 w-3 rounded-sm shrink-0"
											style={{
												backgroundColor: isVisible ? swatchColor : "transparent",
												border: isVisible ? undefined : `1px solid ${swatchColor}`,
											}}
										/>
										{r.name}
									</button>
								);
							})}
					</div>
					<div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2 shrink-0">
						{legendExtra}
						{!showControls && <ChartControls store={store} variant="zoom" />}
						{showControls && <ChartControls store={store} variant="tools" />}
					</div>
				</div>

				{store.loading && (
					<div className="flex items-center justify-center py-8 min-h-[112px]">
						<Spinner size={20} messages={[]} loaderClassName="text-blue-500" />
					</div>
				)}

				{showMessage && (
					<div className="flex flex-col items-center justify-center py-8 min-h-[112px]">
						{store.hasError && !store.isNotConfigured && (
							<>
								<p className="text-red-500 text-sm">Error</p>
								{errorMessage && <p className="text-muted-foreground text-xs mt-1">{errorMessage}</p>}
							</>
						)}
						{store.isNotConfigured && (
							<p className="text-muted-foreground text-sm">Not configured for this structure</p>
						)}
						{!store.hasError && !store.hasData && (
							<p className="text-muted-foreground text-sm">No data for this period</p>
						)}
					</div>
				)}

				{store.loaded && !store.loading && hasVisibleContent && !store.hasError && (
					<HighchartsReact
						ref={chartRef}
						highcharts={Highcharts}
						options={options}
						callback={handleChartCreated}
						immutable={true}
					/>
				)}
			</div>
		);
	},
);
